import React, { useState, useEffect } from 'react';
import { FaLeaf, FaPepperHot } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { menuItemsAPI, categoriesAPI, ordersAPI, restaurantAPI } from '../utils/api';
import { useRestaurantId } from '../utils/restaurantContext';
import { useCart } from '../utils/cartContext';
import { useWaiterGame } from '../utils/waiterGameContext';
import WifiRequiredPopup from '../components/WifiRequiredPopup';
import SectionNav from './SectionNav';
import DishCard from './DishCard';
import OrderModal from './OrderModal';
import CallWaiterButton from './CallWaiterButton';
import DiningIcon from './icons/DiningIcon';
import ImageModal from './ImageModal';
import LoadingSpinner from '../components/LoadingSpinner';
import './MenuPage.css';

function MenuPage() {
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const restaurantId = useRestaurantId();
  const { openInvite } = useWaiterGame();
  
  // Calculate cart item count with safety check
  const cartItemCount = cartItems ? cartItems.reduce((total, item) => total + item.quantity, 0) : 0;
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDish, setSelectedDish] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [helpAnswers, setHelpAnswers] = useState({
    persons: '',
    budget: '',
    popular: '',
    spice: '',
    diet: '',
    cravings: ''
  });
  const [recommended, setRecommended] = useState([]);
  const [notification, setNotification] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedDishImage, setSelectedDishImage] = useState(null);
  const [selectedDishName, setSelectedDishName] = useState(null);
  const [wifiRequired, setWifiRequired] = useState(false);
  const [wifiClientIp, setWifiClientIp] = useState(''); // reserved if we later want to show IP
  const [lastOrderData, setLastOrderData] = useState(null);

  const [maxTables, setMaxTables] = useState(null);

  const [menuLoading, setMenuLoading] = useState(false);
  const [orderSubmitting, setOrderSubmitting] = useState(false);

  useEffect(() => {
    // Without a restaurantId, the backend returns DEFAULT categories.
    // This happens on new devices if user opens the app without coming from a QR link.
    // In that case, redirect to the QR landing so the user can enter via QR.
    if (!restaurantId) {
      navigate('/customer', { replace: true });
      return;
    }

    loadMenuData();
    loadRestaurantMeta();
    // NOTE: We intentionally do NOT show the WiFi popup on page open.
    // Menu browsing should remain available. WiFi is enforced only when the
    // customer tries to place an order or call a waiter.
  }, [restaurantId, navigate]);

  const loadRestaurantMeta = async () => {
    try {
      const res = await restaurantAPI.getById(restaurantId);
      const tables = Number(res?.data?.restaurant?.number_of_tables);
      if (Number.isInteger(tables) && tables > 0) setMaxTables(tables);
      else setMaxTables(null);
    } catch {
      // Don't block menu if restaurant meta fetch fails.
      setMaxTables(null);
    }
  };

  const loadMenuData = async () => {
    try {
      setMenuLoading(true);
      const [itemsResponse, categoriesResponse] = await Promise.all([
        menuItemsAPI.getAll(restaurantId),
        categoriesAPI.getAll(restaurantId),
      ]);

      const loadedItems = itemsResponse.data.menuItems || [];
      const loadedCategories = categoriesResponse.data.categories || [];

      setMenuItems(loadedItems);
      setCategories(loadedCategories);

      if (loadedCategories.length > 0) {
        setSelectedCategory(loadedCategories[0]);
      } else {
        setSelectedCategory(null);
      }
    } catch (error) {
      console.error('Error loading menu:', error);
      // Fallback to empty arrays on error
      setMenuItems([]);
      setCategories([]);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to load menu. Please try again.',
      });
    } finally {
      setMenuLoading(false);
    }
  };

  const filteredItems = selectedCategory
    ? menuItems.filter(item => item.category === selectedCategory)
    : menuItems;

  // Veg/Non-Veg detection (prefer explicit flag; fallback to category/name keywords)
  const getIsVeg = (dish) => {
    if (!dish) return true;

    if (typeof dish.isVeg === 'boolean') return dish.isVeg;
    if (typeof dish.veg === 'boolean') return dish.veg;
    if (typeof dish.vegetarian === 'boolean') return dish.vegetarian;

    const category = String(dish.category || '').toLowerCase();
    const name = String(dish.name || '').toLowerCase();

    if (category.includes('non-veg') || category.includes('non veg') || category.includes('nonveg')) return false;
    if (category.includes('veg')) return true;

    const nonVegKeywords = ['chicken', 'mutton', 'lamb', 'beef', 'prawn', 'shrimp', 'fish', 'egg'];
    return !nonVegKeywords.some((k) => name.includes(k));
  };

  const handleDishClick = (dish) => {
    setSelectedDish(dish);
  };

  const handleView3D = (dish) => {
    setSelectedDishImage(dish.image);
    setSelectedDishName(dish.name);
    setShowImageModal(true);
  };

  const handleOrderClick = (dish) => {
    // Add to cart instead of showing order modal directly
    addToCart(dish);
    setNotification({
      type: 'success',
      message: `${dish.name} added to cart!`,
    });
  };

  const handleOrderSubmit = async (orderData) => {
    if (orderSubmitting) return;

    try {
      setOrderSubmitting(true);
      setLastOrderData(orderData);
      setWifiRequired(false);

      // Pre-check WiFi status before placing order
      const wifiRes = await restaurantAPI.getWifiStatus(restaurantId);
      setWifiClientIp(wifiRes?.data?.clientIp || '');
      if (!wifiRes?.data?.allowed) {
        setWifiRequired(true);
        return;
      }

      const parsedTableNumber = Number(orderData.tableNumber);
      if (maxTables && (parsedTableNumber < 1 || parsedTableNumber > maxTables)) {
        setNotification({
          type: 'error',
          message: `Please enter a valid table number (1-${maxTables}).`,
        });
        return;
      }

      await ordersAPI.create({
        restaurantId: restaurantId,
        customerName: orderData.name,
        mobile: orderData.mobile,
        tableNumber: parsedTableNumber,
        dishId: selectedDish.id,
        dishData: {
          id: selectedDish.id,
          name: selectedDish.name,
          price: selectedDish.price,
          image: selectedDish.image,
        },
        quantity: 1,
      });

      setShowOrderModal(false);
      setSelectedDish(null);
      setNotification({
        type: 'success',
        message: 'Order placed successfully!',
      });

      // Playful waiting-time engagement
      openInvite({ dishName: selectedDish?.name });
    } catch (error) {
      console.error('Error placing order:', error);

      if (error?.status === 403) {
        // Backend blocked ordering due to WiFi-only mode.
        setWifiClientIp('');
        setWifiRequired(true);
        return;
      }

      setNotification({
        type: 'error',
        message: error.message || 'Failed to place order. Please try again.',
      });
    } finally {
      setOrderSubmitting(false);
    }
  };

  const handleHelpYes = () => {
    setShowQuestions(true);
    setShowRecommendations(false);
    setQuestionIndex(0);
  };

  const handleHelpNo = () => {
    setShowHelp(false);
    setShowQuestions(false);
    setShowRecommendations(false);
  };

  const handleHelpChange = (e) => {
    const { name, value } = e.target;
    setHelpAnswers(prev => ({ ...prev, [name]: value }));
  };

  const questions = [
    {
      key: 'persons',
      label: 'How many persons are dining?',
      render: (value, onChange) => (
        <input
          type="number"
          name="persons"
          min="1"
          value={value}
          onChange={onChange}
          required
        />
      )
    },
    {
      key: 'budget',
      label: 'What is your total budget? (₹)',
      render: (value, onChange) => (
        <input
          type="number"
          name="budget"
          min="0"
          value={value}
          onChange={onChange}
          required
        />
      )
    },
    {
      key: 'popular',
      label: 'Do you want to try popular dishes?',
      render: (value, onChange) => (
        <div className="help-radio">
          <label><input type="radio" name="popular" value="Yes" checked={value === 'Yes'} onChange={onChange} required /> Yes</label>
          <label><input type="radio" name="popular" value="No" checked={value === 'No'} onChange={onChange} required /> No</label>
        </div>
      )
    },
    {
      key: 'spice',
      label: 'Preferred spice level?',
      render: (value, onChange) => (
        <select name="spice" value={value} onChange={onChange} required>
          <option value="">Select</option>
          <option value="Mild">Mild</option>
          <option value="Medium">Medium</option>
          <option value="Spicy">Spicy</option>
        </select>
      )
    },
    {
      key: 'diet',
      label: 'Diet preference?',
      render: (value, onChange) => (
        <select name="diet" value={value} onChange={onChange} required>
          <option value="">Select</option>
          <option value="Veg">Veg</option>
          <option value="Non-Veg">Non-Veg</option>
          <option value="Both">Both</option>
        </select>
      )
    },
    {
      key: 'cravings',
      label: 'Any specific cravings or cuisine preference? (optional)',
      render: (value, onChange) => (
        <input
          type="text"
          name="cravings"
          value={value}
          onChange={onChange}
          placeholder="e.g., North Indian, Chinese, desserts"
        />
      )
    }
  ];

  const generateRecommendations = () => {
    const persons = Number(helpAnswers.persons) || 1;
    const budget = parseFloat(helpAnswers.budget) || 0;
    const wantsPopular = helpAnswers.popular === 'Yes';
    const diet = helpAnswers.diet;
    const spice = helpAnswers.spice;
    const cravings = (helpAnswers.cravings || '').toLowerCase();

    const scored = menuItems
      .map(item => {
        let score = 0;

        // Budget fit (per party): prefer items that keep total under budget
        if (budget > 0) {
          const totalCost = item.price * persons;
          if (totalCost <= budget) score += 2;
          else score -= 3; // penalize if over budget
        }

        // Diet preference
        if (diet === 'Veg' && item.diet === 'Veg') score += 3;
        if (diet === 'Non-Veg' && item.diet === 'Non-Veg') score += 3;
        if (diet === 'Both') score += 1;

        // Spice preference
        if (spice && item.spice === spice) score += 2;

        // Popular flag
        if (wantsPopular && item.popular) score += 2;
        if (!wantsPopular && item.popular) score += 0; // neutral

        // Cravings / cuisine / name / description match
        if (cravings) {
          const hay = `${item.name} ${item.description} ${item.cuisine}`.toLowerCase();
          if (hay.includes(cravings)) score += 2;
        }

        return { item, score };
      })
      .filter(s => s.score > -3) // drop very poor matches (e.g., over-budget with penalties)
      .sort((a, b) => b.score - a.score);

    setRecommended(scored.slice(0, 6).map(s => s.item));
  };

  const handleQuestionsSubmit = (e) => {
    e.preventDefault();
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      generateRecommendations();
      setShowQuestions(false);
      setShowRecommendations(true);
    }
  };

  const showLoadingOverlay = menuLoading || orderSubmitting;

  return (
    <div className="menu-page">
      {showLoadingOverlay ? <LoadingSpinner /> : null}
      <WifiRequiredPopup
        isOpen={wifiRequired}
        clientIp={wifiClientIp}
        onClose={() => setWifiRequired(false)}
        onRetry={() => {
          // Re-attempt the last submitted order (if user is now on WiFi)
          if (!selectedDish || !lastOrderData) return Promise.resolve();
          return handleOrderSubmit(lastOrderData);
        }}
        actionLabel="Order"
      />
      <header className="menu-header">
        <button className="header-help-btn" onClick={() => {
          setShowHelp(true);
          setShowQuestions(false);
          setShowRecommendations(false);
        }}>
          <span className="help-icon">?</span>
        </button>
        <div className="menu-logo">
          <span className="menu-logo-top">Menu</span>
          <span className="menu-logo-bottom">Bazzar</span>
        </div>
        <button className="header-cart-btn" onClick={() => navigate(`/menu/${restaurantId}/cart`)}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
          {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
        </button>
      </header>

      {notification && (
        <div className={`menu-notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button
            type="button"
            className="menu-notification-close"
            onClick={() => setNotification(null)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}

      {/* Category tabs removed as requested */}

      <div className="menu-body">
        {/* Large category cards section - horizontally scrollable */}
        {categories.length > 0 && (
          <div className="category-cards-section-large">
            <div className="category-cards-scroll">
              {categories.map((cat, idx) => {
                const categoryItems = menuItems.filter(item => item.category === cat);
                const count = categoryItems.length;
                const firstItem = categoryItems[0];
                // Cycle through colors for category cards
                const colors = [
                  { bg: 'linear-gradient(135deg, #f4a261 0%, #e76f51 100%)', text: '#fff' },
                  { bg: 'linear-gradient(135deg, #f9c74f 0%, #f8961e 100%)', text: '#000' },
                  { bg: 'linear-gradient(135deg, #e76f51 0%, #d62828 100%)', text: '#fff' },
                  { bg: 'linear-gradient(135deg, #f8961e 0%, #f77f00 100%)', text: '#fff' },
                ];
                const colorScheme = colors[idx % colors.length];
                const isSelected = selectedCategory === cat;
                
                return (
                  <div
                    key={cat}
                    className={`category-card-large ${isSelected ? 'selected' : ''}`}
                    style={{ 
                      background: colorScheme.bg, 
                      color: colorScheme.text,
                      animationDelay: `${idx * 0.05}s`
                    }}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    <div className="category-card-large-content">
                      <div className="category-card-large-count">{count}</div>
                      <div className="category-card-large-info">
                        <div className="category-card-large-name">{cat}</div>
                        <div className="category-card-large-subtitle">
                          {firstItem ? firstItem.name : 'Explore'}
                        </div>
                      </div>
                    </div>
                    {firstItem && (
                      <div className="category-card-large-image">
                        <img src={firstItem.image} alt={cat} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dish list section */}
        <div className="menu-content">
          <div className="menu-main">
          {categories.length === 0 ? (
            <div className="empty-category">
              <p>No categories available yet.</p>
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="dishes-list">
              {filteredItems.map((item, idx) => (
                <div 
                  key={item.id} 
                  className="dish-list-card"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="dish-list-image">
                    <img src={item.image} alt={item.name} onClick={() => handleView3D(item)} />
                  </div>
                  <div className="dish-list-info">
                    <div className="dish-list-name-row">
                      <h3 className="dish-list-name">{item.name}</h3>
                      <span
                        className={`dish-list-veg-indicator ${getIsVeg(item) ? 'veg' : 'non-veg'}`}
                        title={getIsVeg(item) ? 'Veg' : 'Non-Veg'}
                        aria-label={getIsVeg(item) ? 'Veg' : 'Non-Veg'}
                      >
                        {getIsVeg(item) ? <FaLeaf /> : <FaPepperHot />}
                      </span>
                    </div>
                    <p className="dish-list-desc">{item.description || 'Delicious dish'}</p>
                    <div className="dish-list-footer">
                      <div className="dish-list-price-section">
                        <span className="dish-list-price">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 2
                          }).format(item.price)}
                        </span>
                      </div>
                      <button className="dish-list-add-btn" onClick={() => handleOrderClick(item)}>
                        Add to cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-category">
              <p>No items available in this category yet.</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {showOrderModal && selectedDish && (
        <OrderModal
          dish={selectedDish}
          submitting={orderSubmitting}
          maxTables={maxTables}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedDish(null);
          }}
          onSubmit={handleOrderSubmit}
        />
      )}

      {showImageModal && selectedDishImage && (
        <ImageModal
          image={selectedDishImage}
          name={selectedDishName}
          onClose={() => {
            setShowImageModal(false);
            setSelectedDishImage(null);
            setSelectedDishName(null);
          }}
        />
      )}

      <CallWaiterButton />

      {showHelp && (
        <div className="help-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            {!showQuestions && !showRecommendations && (
              <div className="help-intro">
                <h3>Are you confused which dish to order?</h3>
                <div className="help-actions">
                  <button className="help-btn primary" onClick={handleHelpYes}>Yes</button>
                  <button className="help-btn" onClick={handleHelpNo}>No</button>
                </div>
              </div>
            )}

            {showQuestions && (
              <form className="help-form" onSubmit={handleQuestionsSubmit}>
                <h4>Question {questionIndex + 1} of {questions.length}</h4>
                <label>
                  {questions[questionIndex].label}
                  {questions[questionIndex].render(helpAnswers[questions[questionIndex].key], handleHelpChange)}
                </label>
                <div className="help-actions spaced">
                  {questionIndex > 0 && (
                    <button type="button" className="help-btn" onClick={() => setQuestionIndex(questionIndex - 1)}>
                      Back
                    </button>
                  )}
                  <button type="submit" className="help-btn primary">
                    {questionIndex === questions.length - 1 ? 'Show Recommendations' : 'Next'}
                  </button>
                  <button type="button" className="help-btn" onClick={() => setShowHelp(false)}>Cancel</button>
                </div>
              </form>
            )}

            {showRecommendations && (
              <div className="help-recommendations">
                <h4>Recommended Dishes</h4>
                <div className="help-reco-grid">
                  {recommended.length === 0 && <p>No matches found. Try adjusting your choices.</p>}
                  {recommended.map(item => (
                    <div key={item.id} className="help-reco-card">
                      <img src={item.image} alt={item.name} />
                      <div className="help-reco-info">
                        <h5>{item.name}</h5>
                        <p className="help-reco-price">
                          {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(item.price)}
                        </p>
                        <div className="help-reco-actions">
                          <button className="help-btn small" onClick={() => handleOrderClick(item)}>Add to Order</button>
                          <button
                            className="help-btn small ghost"
                            onClick={() => handleOrderClick(item)}
                          >
                            Order
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="help-actions">
                  <button className="help-btn" onClick={() => setShowHelp(false)}>Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuPage;